/**
 * A definition's Runs tab: its runs and its not-dispatched matches, newest
 * first (backend `definition_runs`, at most 50), each with its filled run
 * badge (`runRowState`). It polls every 10 s only while mounted (the tab is
 * shown) and a row is still moving (`useDefinitionRuns`).
 *
 * Runs are recorded by definition name, so a proposed definition (not
 * enabled until its MR merges) and a file whose stem is no valid definition
 * name have none, and neither asks the backend.
 */

import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { RunRowBadge } from './RunRowBadge';
import { useDefinitionRuns } from '../hooks/useProjectPanel';
import { isDefinitionName } from '../utils/definitionName';
import { panelErrorMessage } from '../utils/errors';
import { runRowState } from '../utils/runRowState';
import { formatAgo } from '../utils/time';
import { ROUTES } from '@utils/constants';
import type { DefinitionRunRow, MrRef } from '../types/projectPanel.types';

interface DefinitionRunsProps {
  projectId: string;
  /** The definition's name (its file stem). */
  name: string;
  state: 'enabled' | 'proposed';
  /** The open MR changing this definition, for the "pending edit" note. */
  pendingMr: MrRef | null;
}

function mrLabel(url: string): string {
  const iid = /\/merge_requests\/(\d+)\/?$/.exec(url)?.[1];
  return iid === undefined ? 'MR' : `!${iid}`;
}

function RunDetails({ row }: { row: DefinitionRunRow }): ReactElement {
  const text = row.kind === 'match' ? row.reason : row.summary;
  if (row.mr_url === null && !text) return <>—</>;
  return (
    <>
      {row.mr_url !== null && (
        <a href={row.mr_url} target="_blank" rel="noreferrer" className="runs-page__link">
          {mrLabel(row.mr_url)}
        </a>
      )}
      {row.mr_url !== null && text ? ' · ' : null}
      {text}
    </>
  );
}

function RunRow({ row }: { row: DefinitionRunRow }): ReactElement {
  const pipeline = row.pipeline_id === null ? null : `#${String(row.pipeline_id)}`;
  return (
    <tr>
      <td className="def-runs__pipeline">
        {pipeline === null && '—'}
        {pipeline !== null && row.pipeline_url !== null && (
          <a href={row.pipeline_url} target="_blank" rel="noreferrer" className="runs-page__link">
            {pipeline}
          </a>
        )}
        {pipeline !== null && row.pipeline_url === null && pipeline}
      </td>
      <td>
        <RunRowBadge state={runRowState(row)} />
      </td>
      <td className="def-runs__details">
        <RunDetails row={row} />
      </td>
      <td>{row.requested_by ?? '—'}</td>
      <td className="def-runs__age">
        {formatAgo(row.created_at)}
        {row.kind === 'run' && (
          <>
            {' · '}
            <Link to={`${ROUTES.RUNS}/${row.id}`} className="runs-page__link">
              details
            </Link>
          </>
        )}
      </td>
    </tr>
  );
}

function RunsTable({ projectId, name }: { projectId: string; name: string }): ReactElement {
  const { data: rows, error, isLoading, isFetching, refetch } = useDefinitionRuns(projectId, name);
  if (isLoading) return <div className="loading-message">Loading runs…</div>;
  if (rows === undefined) {
    return (
      <div className="error-message" role="alert">
        {panelErrorMessage(error)}{' '}
        <button
          type="button"
          className="def-detail__link-button"
          disabled={isFetching}
          onClick={() => { void refetch(); }}
        >
          Try again
        </button>
      </div>
    );
  }
  return (
    <>
      {error !== null && (
        <div className="error-message" role="alert">
          Couldn&apos;t refresh: {panelErrorMessage(error)}
        </div>
      )}
      {rows.length === 0 ? (
        <p className="def-detail__hint">No runs yet.</p>
      ) : (
        <table className="def-table def-runs">
          <thead>
            <tr>
              <th scope="col">Pipeline</th>
              <th scope="col">State</th>
              <th scope="col">MR · summary</th>
              <th scope="col">Requested by</th>
              <th scope="col">When</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <RunRow key={`${row.kind}:${row.id}`} row={row} />
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function DefinitionRuns({ projectId, name, state, pendingMr }: DefinitionRunsProps): ReactElement {
  if (state === 'proposed') {
    const mr = pendingMr === null ? 'its MR' : `!${String(pendingMr.iid)}`;
    return <p className="def-detail__hint">None, not enabled: runs start once {mr} merges.</p>;
  }
  if (!isDefinitionName(name)) {
    return (
      <p className="def-detail__hint">
        No runs are recorded under {name}: it is not a valid definition name.
      </p>
    );
  }
  const change = pendingMr?.change === 'deleted' ? 'removal' : 'edit';
  return (
    <div className="def-runs-tab">
      {pendingMr !== null && (
        <p className="def-detail__hint">
          Runs use the definition at each pipeline&apos;s commit. The pending {change} applies only
          after !{pendingMr.iid} merges.
        </p>
      )}
      <RunsTable projectId={projectId} name={name} />
    </div>
  );
}
